import React from 'react';
import {
  Typography, Card, CardContent, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter,
  Tooltip
} from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  cohortCell: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  emptyCell: {
    backgroundColor: '#f5f5f5',
  },
  rateCell: {
    fontWeight: 500,
    textAlign: 'center',
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: '#fafafa',
    textAlign: 'center',
  },
  totalCell: {
    textAlign: 'center',
  },
}));

const getRetentionColor = (taux) => {
  if (taux === null || taux === undefined) return 'transparent';
  if (taux >= 50) return 'rgba(76, 175, 80, 0.15)'; // Vert
  if (taux >= 30) return 'rgba(255, 193, 7, 0.15)'; // Jaune
  if (taux >= 15) return 'rgba(255, 152, 0, 0.15)'; // Orange
  return 'rgba(244, 67, 54, 0.1)'; // Rouge clair
};

const getRetentionTextColor = (taux) => {
  if (taux === null || taux === undefined) return '#9e9e9e';
  if (taux >= 50) return '#2e7d32';
  if (taux >= 30) return '#f57c00';
  if (taux >= 15) return '#e65100';
  return '#c62828';
};

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#fafafa',
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#616161',
    padding: '12px 8px',
  },
  body: {
    fontSize: '0.875rem',
    padding: '10px 8px',
  },
}))(TableCell);

const MonthlyCohortTable = ({ data, title = "Cohortes Mensuelles (Cumulé)" }) => {
  const classes = useStyles();
  const cohorts = data?.monthly_cohorts || [];
  const globalAverages = data?.global_averages || {};

  // Déterminer le nombre max de mois de rétention
  const maxMonths = cohorts.reduce((max, cohort) => {
    const months = Object.keys(cohort.retention || {}).length;
    return Math.max(max, months);
  }, 0);

  // Générer les en-têtes M+0, M+1, M+2, etc.
  const monthHeaders = Array.from({ length: maxMonths }, (_, i) => `M+${i}`);

  if (cohorts.length === 0) {
    return (
      <Card style={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
            <Typography color="textSecondary">Aucune donnée de cohorte disponible</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card style={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="caption" color="textSecondary" style={{ marginBottom: 16, display: 'block' }}>
          % cumulé des inscrits du mois ayant commandé au moins une fois
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Cohorte</StyledTableCell>
                <StyledTableCell align="center">Inscrits</StyledTableCell>
                {monthHeaders.map((header) => (
                  <StyledTableCell key={header} align="center">
                    <Tooltip title={header === 'M+0' ? 'Même mois' : `${header.slice(2)} mois après inscription`}>
                      <span>{header}</span>
                    </Tooltip>
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {cohorts.map((cohort) => (
                <TableRow key={cohort.month} hover>
                  <StyledTableCell className={classes.cohortCell}>
                    {cohort.label}
                  </StyledTableCell>
                  <StyledTableCell align="center" className={classes.totalCell}>
                    {cohort.total_inscrits}
                  </StyledTableCell>
                  {monthHeaders.map((header, index) => {
                    const key = `m${index}`;
                    const retentionData = cohort.retention?.[key];
                    const hasData = retentionData !== undefined && retentionData !== null;

                    if (!hasData) {
                      return (
                        <StyledTableCell
                          key={header}
                          align="center"
                          className={classes.emptyCell}
                        >
                          -
                        </StyledTableCell>
                      );
                    }

                    const taux = retentionData.taux;
                    const commandeurs = retentionData.commandeurs;

                    return (
                      <Tooltip
                        key={header}
                        title={`${commandeurs} utilisateurs sur ${cohort.total_inscrits} ont commandé`}
                      >
                        <StyledTableCell
                          align="center"
                          style={{
                            backgroundColor: getRetentionColor(taux),
                            color: getRetentionTextColor(taux),
                            fontWeight: 600,
                          }}
                        >
                          {taux.toFixed(0)}%
                        </StyledTableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
            {/* Ligne Moyenne Globale */}
            {Object.keys(globalAverages).length > 0 && (
              <TableFooter>
                <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                  <StyledTableCell
                    style={{
                      fontWeight: 700,
                      color: '#1565c0',
                      backgroundColor: '#e3f2fd',
                    }}
                  >
                    Moyenne Globale
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    style={{
                      fontWeight: 600,
                      color: '#616161',
                      backgroundColor: '#e3f2fd',
                    }}
                  >
                    -
                  </StyledTableCell>
                  {monthHeaders.map((header, index) => {
                    const key = `m${index}`;
                    const avgData = globalAverages[key];

                    if (!avgData) {
                      return (
                        <StyledTableCell
                          key={header}
                          align="center"
                          style={{ backgroundColor: '#e3f2fd' }}
                        >
                          -
                        </StyledTableCell>
                      );
                    }

                    const taux = avgData.taux;

                    return (
                      <Tooltip
                        key={header}
                        title={`Moyenne sur ${avgData.nb_cohortes} cohorte${avgData.nb_cohortes > 1 ? 's' : ''}`}
                      >
                        <StyledTableCell
                          align="center"
                          style={{
                            backgroundColor: '#e3f2fd',
                            color: '#1565c0',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                          }}
                        >
                          {taux.toFixed(0)}%
                        </StyledTableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>

        {/* Légende */}
        <Box mt={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="caption" color="textSecondary" style={{ marginRight: 8 }}>
            Légende :
          </Typography>
          <Box display="flex" alignItems="center" mr={2}>
            <Box
              style={{
                width: 16,
                height: 16,
                backgroundColor: getRetentionColor(60),
                borderRadius: 2,
                marginRight: 4,
              }}
            />
            <Typography variant="caption" style={{ color: getRetentionTextColor(60) }}>
              ≥50%
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" mr={2}>
            <Box
              style={{
                width: 16,
                height: 16,
                backgroundColor: getRetentionColor(35),
                borderRadius: 2,
                marginRight: 4,
              }}
            />
            <Typography variant="caption" style={{ color: getRetentionTextColor(35) }}>
              30-49%
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" mr={2}>
            <Box
              style={{
                width: 16,
                height: 16,
                backgroundColor: getRetentionColor(20),
                borderRadius: 2,
                marginRight: 4,
              }}
            />
            <Typography variant="caption" style={{ color: getRetentionTextColor(20) }}>
              15-29%
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Box
              style={{
                width: 16,
                height: 16,
                backgroundColor: getRetentionColor(10),
                borderRadius: 2,
                marginRight: 4,
              }}
            />
            <Typography variant="caption" style={{ color: getRetentionTextColor(10) }}>
              &lt;15%
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonthlyCohortTable;
